import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { authApi } from '../services/saasApi';
import { User } from '../services/types';
import {
  getStoredUser,
  saveUser,
  removeUser,
  removeToken,
  removeRefreshToken,
} from '../utils/localStorage';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  loginWithCode: (email: string, code: string) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string | undefined, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** 清除所有认证状态（本地存储 + 通知后端清除 cookie） */
function clearLocalAuth() {
  // 清除可能残留的旧版 localStorage token
  removeToken();
  removeRefreshToken();
  removeUser();
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 初始化：通过 /api/auth/me 验证 cookie 有效性（cookie 由浏览器自动携带）
  useEffect(() => {
    const initAuth = async () => {
      // 先用缓存的用户信息快速渲染（乐观更新）
      const storedUserData = getStoredUser<User>();
      if (storedUserData) {
        setUser(storedUserData);
        setIsAuthenticated(true);
      }

      try {
        // 验证 cookie 有效性（withCredentials 自动携带 cookie）
        const response = await authApi.getMe();
        const userData = response.data.data || response.data;
        if (userData && userData.id) {
          setUser(userData);
          setIsAuthenticated(true);
          saveUser(userData);
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          // Cookie 无效或过期，尝试 refresh（refresh token cookie 自动携带）
          try {
            const refreshResponse = await authApi.refreshToken('');
            const { user: userData } = refreshResponse.data;
            if (userData) {
              setUser(userData);
              setIsAuthenticated(true);
              saveUser(userData);
            }
          } catch {
            clearLocalAuth();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          clearLocalAuth();
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const loginWithCode = useCallback(async (email: string, code: string) => {
    const response = await authApi.verifyCode(email, code);
    if (!response.data.user) {
      throw new Error('验证码错误');
    }

    const { user: userData } = response.data;
    // token 已由后端通过 httpOnly cookie 设置，仅保存用户信息到本地
    saveUser(userData);
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const loginWithTokens = useCallback(
    (_accessToken: string, _refreshToken: string | undefined, userData: User) => {
      // token 由 cookie 管理，仅保存用户信息
      saveUser(userData);
      setUser(userData);
      setIsAuthenticated(true);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      // 通知后端清除 httpOnly cookie
      await authApi.logout();
    } catch {
      // 忽略错误，继续清除本地状态
    }
    clearLocalAuth();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getMe();
      const userData = response.data.data || response.data;
      if (userData && userData.id) {
        setUser(userData);
        saveUser(userData);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    loginWithCode,
    loginWithTokens,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
